import type { ApiClient } from '../../rest-api/generated/laravel-typed-openapi';

/**
 * The organization's billing over REST: the `/subscription/*` routes from PR #1239.
 *
 * Every route acts on the active organization and refuses anyone without billing rights, so only
 * the owner's screens call these. They also fail on Free, which has no Stripe subscription: callers
 * enable them only for a paid plan.
 *
 * The generated types say every field is a string. Scramble can't see through the Stripe objects
 * the actions return, and the real values are numbers: amounts in cents, dates in Unix seconds.
 * These functions are the only place the raw response is read, and they read it as it really is.
 */

/** Unix seconds, as Stripe sends dates, to an ISO string. */
const fromUnixSeconds = (value: unknown): string | null => {
  const seconds = Number(value);

  return Number.isFinite(seconds) && seconds > 0 ? new Date(seconds * 1000).toISOString() : null;
};

const toNumber = (value: unknown): number => {
  const n = Number(value);

  return Number.isFinite(n) ? n : 0;
};

export type UpcomingInvoice = {
  /** What will be charged, in cents. */
  amountDue: number;
  /** Before tax, in cents. */
  subtotal: number;
  /** After tax, in cents. More than `subtotal` when VAT is added. */
  total: number;
  /** ISO 4217, upper case: "EUR". */
  currency: string;
  /** When it is charged: the end of the current period. */
  chargedAt: string | null;
};

export const fetchUpcomingInvoice = async (apiClient: ApiClient): Promise<UpcomingInvoice> => {
  const { data } = await apiClient.get('/subscription/upcoming-invoice');

  return {
    amountDue: toNumber(data.amount_due),
    subtotal: toNumber(data.subtotal),
    total: toNumber(data.total),
    currency: String(data.currency).toUpperCase(),
    chargedAt: fromUnixSeconds(data.period_end),
  };
};

export type ScheduledPlanChange = {
  /** The plan slug the organization moves to. Stripe's price ids are the plan slugs. */
  upcomingPlan: string;
  /** When the switch happens. */
  effectiveAt: string | null;
};

/** The downgrade waiting for the end of the period, or `null` when nothing is scheduled. */
export const fetchScheduledUpdate = async (apiClient: ApiClient): Promise<ScheduledPlanChange | null> => {
  const response = await apiClient.get('/subscription/scheduled-update', { withResponse: true });

  // The route answers 404 for "nothing scheduled", which is the usual case, not an error.
  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Couldn't load the scheduled plan change (${response.status})`);
  }

  const { data } = response.data as { data: { upcoming_plan: unknown; effective_at: unknown } };

  return { upcomingPlan: String(data.upcoming_plan), effectiveAt: fromUnixSeconds(data.effective_at) };
};

/**
 * Stop the plan renewing. It stays active until the end of the period, then the organization moves
 * to Free. A trial ends at once instead: the backend deletes a trialing subscription.
 */
export const cancelRenewal = async (apiClient: ApiClient): Promise<{ endsAt: string | null }> => {
  const { data } = await apiClient.post('/subscription/cancel');

  return { endsAt: data.ends_at ? new Date(String(data.ends_at)).toISOString() : null };
};

/** Undo `cancelRenewal` before the period ends. */
export const resumeRenewal = async (apiClient: ApiClient): Promise<void> => {
  await apiClient.post('/subscription/resume');
};

/** Drop a scheduled downgrade: the organization stays on its current plan. */
export const cancelScheduledUpdate = async (apiClient: ApiClient): Promise<void> => {
  await apiClient.post('/subscription/scheduled-update/cancel');
};

export type InvoiceStatus = 'draft' | 'open' | 'paid' | 'uncollectible' | 'void';

export type Invoice = {
  id: string;
  /** Stripe's invoice number, "MDC-2026-0142". `null` on a draft. */
  number: string | null;
  status: InvoiceStatus | null;
  /** In cents. */
  total: number;
  currency: string;
  createdAt: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  /** The PDF to download. `null` until Stripe has finalized the invoice. */
  pdfUrl: string | null;
};

export type InvoicePage = {
  invoices: Invoice[];
  /** Pass back as `startingAfter` for the next page; `null` on the last one. */
  nextCursor: string | null;
};

const INVOICE_STATUSES: readonly string[] = ['draft', 'open', 'paid', 'uncollectible', 'void'];

const toInvoice = (raw: Record<string, unknown>): Invoice => ({
  id: String(raw.id),
  number: raw.number ? String(raw.number) : null,
  status: INVOICE_STATUSES.includes(String(raw.status)) ? (raw.status as InvoiceStatus) : null,
  total: toNumber(raw.total),
  currency: String(raw.currency).toUpperCase(),
  createdAt: fromUnixSeconds(raw.created),
  periodStart: fromUnixSeconds(raw.period_start),
  periodEnd: fromUnixSeconds(raw.period_end),
  pdfUrl: raw.invoice_pdf ? String(raw.invoice_pdf) : null,
});

/**
 * One page of the organization's invoices, newest first.
 *
 * The backend filters Stripe's page down to this organization, so a page can hold fewer rows than
 * asked for, or none, and still have more after it. Page on `nextCursor`, never on the row count.
 * The route also needs an active plan: an organization that has cancelled can't read what it paid
 * (MDC-4534).
 */
export const fetchInvoices = async (apiClient: ApiClient, startingAfter?: string): Promise<InvoicePage> => {
  const response = (await apiClient.get('/subscription/invoices', {
    query: { limit: 10, ...(startingAfter ? { starting_after: startingAfter } : {}) },
  })) as unknown as { data: Record<string, unknown>[]; has_more: unknown; next_starting_after: unknown };

  return {
    invoices: (response.data ?? []).map(toInvoice),
    nextCursor: response.has_more && response.next_starting_after ? String(response.next_starting_after) : null,
  };
};

/**
 * A one-off link to Stripe's billing portal, where the owner changes the card, the billing address
 * and the VAT ID. Stripe sends them back to `returnUrl`.
 */
export const fetchBillingPortalUrl = async (apiClient: ApiClient, returnUrl: string): Promise<string> => {
  const { url } = await apiClient.get('/subscription/portal', { query: { return_url: returnUrl } });

  return url;
};

export type PaymentMethod = {
  /** "card", or "sepa_debit" and the like. */
  type: string;
  /** A card's only: "visa". */
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
};

type RawPaymentMethod = {
  type: string;
  brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
} | null;

const toPaymentMethod = (method: RawPaymentMethod): PaymentMethod | null =>
  method
    ? {
        type: method.type,
        brand: method.brand,
        last4: method.last4,
        expMonth: method.exp_month,
        expYear: method.exp_year,
      }
    : null;

export type BillingAddress = {
  line1: string | null;
  line2: string | null;
  city: string | null;
  postalCode: string | null;
  state: string | null;
  /** ISO 3166-1 alpha-2. */
  country: string | null;
};

/** What the invoices print, and where the receipts go. */
export type BillingDetails = {
  /** A person's or a company's. */
  name: string | null;
  /** Where Stripe sends the receipts. */
  email: string | null;
  phone: string | null;
  address: BillingAddress | null;
  /** The VAT IDs; the app shows and edits the first. */
  taxIds: { type: string; value: string }[];
};

export type BillingInfo = {
  /**
   * What Stripe charges the organization's plan to: the subscription's payment method, else the
   * owner's default. `null` when there is none, as on a trial started without a card, which
   * Stripe cancels at its end.
   */
  paymentMethod: PaymentMethod | null;
  /** `null` while the owner has no Stripe customer. */
  details: BillingDetails | null;
};

type RawBillingDetails = {
  name: string | null;
  email: string | null;
  phone: string | null;
  address: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    postal_code: string | null;
    state: string | null;
    country: string | null;
  } | null;
  tax_ids: { type: string; value: string }[];
} | null;

const toBillingInfo = (data: { payment_method: RawPaymentMethod; billing_details: RawBillingDetails }): BillingInfo => {
  const raw = data.billing_details;

  return {
    paymentMethod: toPaymentMethod(data.payment_method),
    details: raw
      ? {
          name: raw.name,
          email: raw.email,
          phone: raw.phone,
          address: raw.address
            ? {
                line1: raw.address.line1,
                line2: raw.address.line2,
                city: raw.address.city,
                postalCode: raw.address.postal_code,
                state: raw.address.state,
                country: raw.address.country,
              }
            : null,
          taxIds: raw.tax_ids,
        }
      : null,
  };
};

/** The card on file and the billing details of the organization's Stripe customer. */
export const fetchBillingInfo = async (apiClient: ApiClient): Promise<BillingInfo> => {
  const { data } = await apiClient.get('/subscription/billing-details');

  return toBillingInfo(data);
};

export type BillingDetailsInput = {
  name: string;
  email: string;
  address: Omit<BillingAddress, 'country'> & { country: string };
  /** The VAT ID; `null` removes it. Its type comes from the country. */
  taxId: string | null;
};

/**
 * Changes the billing details. A VAT ID Stripe refuses, or one the country can't take, fails with
 * a 422 carrying the message on `tax_id` (see `billingFieldErrors`), and nothing changes.
 */
export const updateBillingDetails = async (apiClient: ApiClient, input: BillingDetailsInput): Promise<BillingInfo> => {
  const { data } = await apiClient.put('/subscription/billing-details', {
    body: {
      name: input.name,
      email: input.email,
      address: {
        line1: input.address.line1,
        line2: input.address.line2,
        city: input.address.city,
        postal_code: input.address.postalCode,
        state: input.address.state,
        country: input.address.country,
      },
      tax_id: input.taxId,
    },
  });

  return toBillingInfo(data);
};

/**
 * The field messages of a 422 from `updateBillingDetails`, keyed as the backend names them
 * (`tax_id`, `address.country`). Empty for any other failure.
 */
export const billingFieldErrors = (error: unknown): Record<string, string> => {
  const response = (error as { response?: { status?: number; data?: { errors?: unknown } } } | null)?.response;
  const errors = response?.status === 422 ? response.data?.errors : null;

  if (!errors || typeof errors !== 'object') {
    return {};
  }

  return Object.fromEntries(
    Object.entries(errors as Record<string, unknown>)
      .map(([field, messages]) => [field, Array.isArray(messages) ? messages[0] : messages])
      .filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
  );
};

/** A setup Checkout Session for the card form: its id, and the secret Stripe.js opens it with. */
export type SetupSession = { id: string; clientSecret: string };

/**
 * A setup-mode Checkout Session on the owner's Stripe customer, for the card form (Elements with
 * the Checkout Sessions API). Its client secret goes to Stripe's `CheckoutElementsProvider`;
 * confirming it saves the card, which `savePaymentMethod` then makes the one charged.
 *
 * `returnUrl` is where a payment method that leaves the page (a bank's own confirmation) comes
 * back to; Stripe swaps `{CHECKOUT_SESSION_ID}` in it for the session id.
 */
export const createSetupSession = async (apiClient: ApiClient, returnUrl: string): Promise<SetupSession> => {
  const { data } = await apiClient.post('/subscription/setup-session', { body: { return_url: returnUrl } });

  return { id: data.id, clientSecret: data.client_secret };
};

/**
 * Makes the card a completed setup Checkout Session saved the one the organization is charged to,
 * on the subscription and on the customer. Fails (422) for a session of another organization, and
 * for one that hasn't saved a card yet: `isSetupRefused` tells those from a failure to save.
 */
export const savePaymentMethod = async (
  apiClient: ApiClient,
  checkoutSessionId: string,
): Promise<PaymentMethod | null> => {
  const { data } = await apiClient.post('/subscription/payment-method', {
    body: { checkout_session: checkoutSessionId },
  });

  return toPaymentMethod(data.payment_method);
};

/**
 * `savePaymentMethod` failed because the session saved no card the organization can use (a 422),
 * rather than because the card couldn't be made the one charged.
 */
export const isSetupRefused = (error: unknown): boolean =>
  (error as { response?: { status?: number } } | null)?.response?.status === 422;

/** The paid plans the billing routes can switch between. Free is reached by cancelling renewal. */
export type PaidPlanSlug = 'essentials' | 'plus' | 'pro';

export type PreviewLine = {
  description: string;
  /** In cents; negative for credit. */
  amount: number;
  /** A proration: part of what is charged today. The rest is the next renewal. */
  isProration: boolean;
  periodStart: string | null;
  periodEnd: string | null;
};

export type UpgradePreview = {
  currency: string;
  /** Before tax, in cents, for the whole preview. */
  subtotal: number;
  /** After tax, in cents. */
  total: number;
  lines: PreviewLine[];
};

/**
 * What an upgrade to `plan` costs, from Stripe: the new plan for the rest of the period and the
 * credit for the unused part of the current one (the proration lines, charged today), then the
 * next renewal. Never computed on the client.
 */
export const previewUpgrade = async (apiClient: ApiClient, plan: PaidPlanSlug): Promise<UpgradePreview> => {
  const { data } = await apiClient.post('/subscription/preview-upgrade', { body: { plan, proration_time: 'now' } });
  const raw = data as unknown as { currency: unknown; subtotal: unknown; total: unknown; lines: unknown };
  const lines = Array.isArray(raw.lines) ? (raw.lines as Record<string, unknown>[]) : [];

  return {
    currency: String(raw.currency).toUpperCase(),
    subtotal: toNumber(raw.subtotal),
    total: toNumber(raw.total),
    lines: lines.map((line) => {
      const period = (line.period ?? {}) as { start?: unknown; end?: unknown };

      return {
        description: String(line.description ?? ''),
        amount: toNumber(line.amount),
        isProration: line.proration === true,
        periodStart: fromUnixSeconds(period.start),
        periodEnd: fromUnixSeconds(period.end),
      };
    }),
  };
};

/** Move to a higher plan now. Stripe invoices the prorated difference at once. */
export const upgradePlan = async (apiClient: ApiClient, plan: PaidPlanSlug): Promise<void> => {
  await apiClient.post('/subscription/upgrade', { body: { plan } });
};

/** Schedule a lower plan for the next renewal. Nothing is charged today. */
export const downgradePlan = async (
  apiClient: ApiClient,
  plan: PaidPlanSlug,
): Promise<{ effectiveAt: string | null }> => {
  const { data } = await apiClient.post('/subscription/downgrade', { body: { plan } });

  return { effectiveAt: fromUnixSeconds(data.effective_at) };
};

export type CheckoutSession = {
  /** Stripe's checkout session id: "cs_test_…". */
  id: string;
  /** What Stripe's embedded checkout mounts with. */
  clientSecret: string;
};

/**
 * Start a first paid plan with Stripe's embedded checkout. The subscription belongs to the active
 * organization; its owner is the Stripe customer.
 *
 * `returnUrl` is where Stripe sends the owner back after a payment method that leaves the page.
 * Card and SEPA don't, so most checkouts finish in place.
 */
export const createCheckoutSession = async (
  apiClient: ApiClient,
  plan: PaidPlanSlug,
  returnUrl: string,
): Promise<CheckoutSession> => {
  const { session } = await apiClient.post('/subscription/checkout', {
    body: { plan, ui_mode: 'embedded', return_url: returnUrl },
  });

  if (typeof session?.client_secret !== 'string' || session.client_secret === '') {
    throw new Error('Stripe returned no checkout to show');
  }

  return { id: String(session.id), clientSecret: session.client_secret };
};

export const CHECKOUT_STATUS = {
  open: 'open',
  complete: 'complete',
  expired: 'expired',
} as const;

export type CheckoutStatus = (typeof CHECKOUT_STATUS)[keyof typeof CHECKOUT_STATUS];

export type CheckoutResult = {
  /** `open` until the owner pays, `complete` after, `expired` once the page stops working. */
  status: CheckoutStatus;
  /** When the new plan renews, once Stripe has made the subscription. */
  renewalDate: string | null;
};

/** How a checkout ended, for the owner coming back from a payment that left the page. */
export const fetchCheckoutResult = async (apiClient: ApiClient, sessionId: string): Promise<CheckoutResult> => {
  const { data } = await apiClient.get('/subscription/checkout/{session_id}', {
    path: { session_id: sessionId },
    query: { session_id: sessionId },
  });
  const status = Object.values(CHECKOUT_STATUS).find((value) => value === data.status) ?? CHECKOUT_STATUS.open;

  return { status, renewalDate: data.renewal_date ?? null };
};
